import React, { useEffect, useState } from "react";
import qs from "qs";
import { connect } from "react-redux";
import { Icon, Input, Alert } from "antd";
import { compose, withProps } from "recompose";
import { Form, FormItem } from "@worcket/common/components/controls";
import { withForm, isLoading } from "@worcket/common/hocs";
import { useTranslation } from "react-i18next";

import Centered from "../../components/display/Centered";
import {
  changeRecoverPassword,
  validateRecoverPasswordCode,
  CHANGE_RECOVER_PASSWORD
} from "../../store/actions/session";
import logo from "../../assets/logo-black.svg";
import Button from "../../components/Button";
import {
  passwordValidateRules,
  passwordCompareRules
} from "../../utils/password";

import styled from "styled-components/macro";

const Container = styled(Form)`
  width: 300px;

  .logo {
    margin-bottom: 40px;
    display: flex;
    justify-content: center;

    img {
      max-width: 160px;
    }
  }

  .title {
    display: block;
    text-align: center;
    margin-bottom: 14px;
  }

  .change-password-btn {
    width: 100%;
  }

  .success-alert {
    margin-top: 16px;
  }
`;

const PasswordRecoveryChange = ({
  code,
  form,
  onSubmit,
  loading,
  error,
  validateRecoverPasswordCode
}) => {
  const { t } = useTranslation();
  const [validating, setValidating] = useState(Boolean(code));
  const [codeError, setCodeError] = useState(error);

  useEffect(() => {
    if (!code) return;

    let mounted = true;

    validateRecoverPasswordCode(code)
      .then(() => {
        if (!mounted) return;

        setCodeError(false);
        setValidating(false);
      })
      .catch(() => {
        if (!mounted) return;

        setCodeError(true);
        setValidating(false);
      });

    return () => {
      mounted = false;
    };
  }, [code, validateRecoverPasswordCode]);

  return (
    <Centered>
      <Container onSubmit={onSubmit}>
        <div className="logo">
          <img src={logo} alt="Worcket" />
        </div>
        <h2 className="title">
          {t("components:PasswordRecoveryChange.title")}
        </h2>
        {validating ? null : codeError ? (
          <Alert
            message={t("auth:recoveryPassword.errorLink")}
            type="error"
            showIcon
          />
        ) : (
          <div>
            <FormItem
              form={form}
              name="password"
              rules={passwordValidateRules}
              render={
                <Input.Password
                  autoComplete="new-password"
                  prefix={
                    <Icon type="lock" style={{ color: "rgba(0,0,0,.25)" }} />
                  }
                  placeholder={t("auth:recoveryPassword.newPassword")}
                />
              }
            />
            <FormItem
              form={form}
              name="repeatedPassword"
              rules={passwordCompareRules(form, "password")}
              render={
                <Input.Password
                  autoComplete="new-password"
                  prefix={
                    <Icon type="lock" style={{ color: "rgba(0,0,0,.25)" }} />
                  }
                  placeholder={t("auth:signup.repeatPassword")}
                />
              }
            />
            <Button
              loading={loading}
              className="change-password-btn"
              size="xl"
              type="primary"
              htmlType="submit"
            >
              {t("components:PasswordRecoveryChange.button")}
            </Button>
          </div>
        )}
      </Container>
    </Centered>
  );
};

const enhancer = compose(
  withProps(({ location }) => {
    const { code } = qs.parse(location.search, { ignoreQueryPrefix: true });
    return {
      code,
      error: !code
    };
  }),
  connect(
    undefined,
    { changeRecoverPassword, validateRecoverPasswordCode }
  ),
  isLoading(CHANGE_RECOVER_PASSWORD),
  withForm({
    onSubmit: (values, { changeRecoverPassword, code }) => {
      changeRecoverPassword({ newPassword: values.password, code });
    }
  })
);

export default enhancer(PasswordRecoveryChange);
